import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import { uploadService } from '../../../api/services/uploadService';
import { useOps } from '../../../hooks/useOps';
import type { FileStatusItem } from '../types';
import { computeStats } from '../utils';

interface UseFileStatusOptions {
  docType: string;
  enabled?: boolean;
}

interface UseFileStatusReturn {
  files: FileStatusItem[];
  stats: ReturnType<typeof computeStats>;
  isLoading: boolean;
  isRefetching: boolean;
  error: Error | null;
  refetch: () => void;
  invalidate: () => void;
}

const QUERY_KEY_PREFIX = 'file-status';
const EMPTY_ARRAY: FileStatusItem[] = [];

export function useFileStatus({
  docType,
  enabled = true,
}: UseFileStatusOptions): UseFileStatusReturn {
  const { getCurrentOpsConfig } = useOps();
  const queryClient = useQueryClient();

  const opsConfig = getCurrentOpsConfig();
  const dbName = opsConfig?.dbName;

  const queryKey = useMemo(
    () => [QUERY_KEY_PREFIX, docType, dbName] as const,
    [docType, dbName]
  );

  const { data, isLoading, isFetching, error, refetch } = useQuery({
    queryKey,
    queryFn: async (): Promise<FileStatusItem[]> => {
      if (!dbName || !docType) {
        return [];
      }

      const datetime = new Date().toISOString().slice(0, 10);
      const response = await uploadService.getFileStatus({
        datetime,
        docType,
        dbName,
      });

      return (response.data ?? []) as FileStatusItem[];
    },
    enabled: enabled && Boolean(dbName) && Boolean(docType),
    staleTime: 3000,
    gcTime: 2 * 60 * 1000,
    retry: 2,
  });

  const files = data ?? EMPTY_ARRAY;

  const stats = useMemo(() => computeStats(files), [files]);

  const invalidate = useCallback(() => {
    queryClient.invalidateQueries({ queryKey });
  }, [queryClient, queryKey]);

  const handleRefetch = useCallback(() => {
    refetch();
  }, [refetch]);

  return {
    files,
    stats,
    isLoading,
    isRefetching: isFetching && !isLoading,
    error: error as Error | null,
    refetch: handleRefetch,
    invalidate,
  };
}
